import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styles from './ContextMenuWindow.module.css';
import Item from '../Item/Item';
import useAccessibleFocus from '../../hooks/useAccessibleFocus';
import { useLang } from '../../hooks/useLang';
export default function ContextMenuWindow(props) {
    var _a, _b, _c;
    const [open, setOpen] = useState(true);
    const itemsRefs = useAccessibleFocus(props.items);
    const lang = useLang();
    function fireEvent(event, item) {
        if (props[event])
            props[event](item);
    }
    const handleClick = useCallback(() => {
        setOpen(false);
        if (props.animated === false)
            props.onTransitionEnd();
    }, []);
    const onKeyUp = useCallback((event) => {
        event.preventDefault();
        if (event.key === 'Escape') {
            handleClick();
        }
    }, []);
    useEffect(() => {
        window.addEventListener("click", handleClick);
        window.addEventListener("keyup", onKeyUp);
        fireEvent('onAfterOpen');
        return () => {
            fireEvent('onClose');
            window.removeEventListener("click", handleClick);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);
    const adaptiveWidth = () => `${(props.position.x * 100) / document.documentElement.scrollWidth}vw`;
    const adaptiveHeight = () => `${(props.position.y * 100) / document.documentElement.scrollHeight}vh`;
    const screenWidth = useCallback(() => {
        if (props.adaptive === false)
            return props.position.x + document.documentElement.scrollLeft;
        return adaptiveWidth();
    }, [props.adaptive]);
    const screenHeight = useCallback(() => {
        if (props.adaptive === false)
            return props.position.y + document.documentElement.scrollTop;
        return adaptiveHeight();
    }, [props.adaptive]);
    const screenSize = { width: screenWidth(), height: screenHeight() };
    function getAnimationFromProps(direction) {
        var _a, _b;
        if (typeof props.animated !== "boolean")
            return ((_b = (_a = props.animated) === null || _a === void 0 ? void 0 : _a.animation) !== null && _b !== void 0 ? _b : 'zoom') + direction;
        if (props.animated)
            return `zoom${direction}`;
    }
    const originClassName = useMemo(() => {
        var _a, _b;
        return `transformOrigin-${(_a = props.position) === null || _a === void 0 ? void 0 : _a.origin.x}-${(_b = props.position) === null || _b === void 0 ? void 0 : _b.origin.y}`;
    }, [(_a = props.position) === null || _a === void 0 ? void 0 : _a.origin.x, (_b = props.position) === null || _b === void 0 ? void 0 : _b.origin.y]);
    const composeDefaultVariants = useCallback(() => {
        var _a, _b, _c, _d, _e, _f;
        return `${styles[`${((_b = (_a = props.variant) === null || _a === void 0 ? void 0 : _a.theme) !== null && _b !== void 0 ? _b : "light")}-${((_d = (_c = props.variant) === null || _c === void 0 ? void 0 : _c.opacity) !== null && _d !== void 0 ? _d : "transparent")}`]} ${styles[(_f = (_e = props.variant) === null || _e === void 0 ? void 0 : _e.elevation) !== null && _f !== void 0 ? _f : "raised"]}`;
    }, [props.variant]);
    const containerStyle = useMemo(() => {
        var _a, _b, _c, _d;
        if (props.animated === false)
            return `${styles.container} ${(_b = (_a = props.menuClassName) === null || _a === void 0 ? void 0 : _a.container) !== null && _b !== void 0 ? _b : composeDefaultVariants()}`;
        return `${styles['container']}  ${styles[originClassName]} ${styles[getAnimationFromProps(open ? "In" : "Out")]} ${(_d = (_c = props.menuClassName) === null || _c === void 0 ? void 0 : _c.container) !== null && _d !== void 0 ? _d : composeDefaultVariants()}`;
    }, [open]);
    const animationStyle = useMemo(() => {
        var _a, _b;
        if (typeof props.animated !== "boolean")
            return { animationDuration: (_b = (_a = props.animated) === null || _a === void 0 ? void 0 : _a.duration) !== null && _b !== void 0 ? _b : '0.2s' };
        if (props.animated)
            return { animationDuration: '0.2s' };
        return {};
    }, [open]);
    function transitionEnd() {
        if (!open) {
            fireEvent('onOutAnimationEnd');
            props.onTransitionEnd();
            return;
        }
        fireEvent('onInAnimationEnd');
    }
    return (React.createElement("div", { ref: props.containerRef, className: containerStyle, onAnimationEnd: transitionEnd, role: 'menu', "aria-label": lang.contextmenu, style: Object.assign(Object.assign({ top: screenSize.height, left: screenSize.width }, animationStyle), (_c = props.menuStyle) === null || _c === void 0 ? void 0 : _c.container) }, props.items.map((item, index) => {
        var _a, _b;
        return (React.createElement(Item, { item: item, key: index, index: index, itemRef: itemsRefs[index], className: (_a = props.menuClassName) === null || _a === void 0 ? void 0 : _a.row, style: (_b = props.menuStyle) === null || _b === void 0 ? void 0 : _b.row, onItemHoverIn: props.onItemHoverIn, onItemHoverOut: props.onItemHoverOut, variant: props.variant }));
    })));
}
